import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const HoldingsTable = ({ holdings, onEditPosition, onAddPosition }) => {
  const [sortField, setSortField] = useState('value');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filterType, setFilterType] = useState('all');

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    })?.format(value);
  };

  const formatPercentage = (value) => {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value?.toFixed(2)}%`;
  };

  const getPerformanceColor = (value) => {
    if (value > 0) return 'text-success';
    if (value < 0) return 'text-error';
    return 'text-muted-foreground';
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const filteredHoldings = holdings?.filter(holding => {
    if (filterType === 'all') return true;
    if (filterType === 'gainers') return holding?.unrealizedPnL > 0;
    if (filterType === 'losers') return holding?.unrealizedPnL < 0;
    return true;
  });

  const sortedHoldings = [...filteredHoldings]?.sort((a, b) => {
    const aValue = a?.[sortField];
    const bValue = b?.[sortField];
    
    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : -1;
    }
    return aValue < bValue ? 1 : -1;
  });

  const SortIcon = ({ field }) => {
    if (sortField !== field) {
      return <Icon name="ArrowUpDown" size={14} className="text-muted-foreground" />;
    }
    return (
      <Icon 
        name={sortDirection === 'asc' ? 'ArrowUp' : 'ArrowDown'} 
        size={14} 
        className="text-primary" 
      />
    );
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-1">
      <div className="p-6 border-b border-border">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h2 className="text-xl font-semibold text-card-foreground">Holdings</h2>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex bg-muted rounded-md p-1">
              {[
                { key: 'all', label: 'All' },
                { key: 'gainers', label: 'Gainers' },
                { key: 'losers', label: 'Losers' }
              ]?.map((filter) => (
                <button
                  key={filter?.key}
                  onClick={() => setFilterType(filter?.key)}
                  className={`px-3 py-1 text-xs font-medium rounded transition-standard ${
                    filterType === filter?.key
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {filter?.label}
                </button>
              ))}
            </div>
            
            <Button 
              variant="default" 
              size="sm" 
              iconName="Plus" 
              iconPosition="left"
              onClick={onAddPosition}
            >
              Add Position
            </Button>
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground">
                <button 
                  onClick={() => handleSort('symbol')}
                  className="flex items-center space-x-2 hover:text-foreground transition-standard"
                >
                  <span>Asset</span>
                  <SortIcon field="symbol" />
                </button>
              </th>
              <th className="text-right p-4 text-sm font-medium text-muted-foreground">
                <button 
                  onClick={() => handleSort('quantity')}
                  className="flex items-center justify-end space-x-2 hover:text-foreground transition-standard w-full"
                >
                  <span>Quantity</span>
                  <SortIcon field="quantity" />
                </button>
              </th>
              <th className="text-right p-4 text-sm font-medium text-muted-foreground">
                <button 
                  onClick={() => handleSort('currentPrice')}
                  className="flex items-center justify-end space-x-2 hover:text-foreground transition-standard w-full"
                >
                  <span>Price</span>
                  <SortIcon field="currentPrice" />
                </button>
              </th>
              <th className="text-right p-4 text-sm font-medium text-muted-foreground">
                <button 
                  onClick={() => handleSort('value')}
                  className="flex items-center justify-end space-x-2 hover:text-foreground transition-standard w-full"
                >
                  <span>Value</span>
                  <SortIcon field="value" />
                </button>
              </th>
              <th className="text-right p-4 text-sm font-medium text-muted-foreground">
                <button 
                  onClick={() => handleSort('costBasis')}
                  className="flex items-center justify-end space-x-2 hover:text-foreground transition-standard w-full"
                >
                  <span>Cost Basis</span>
                  <SortIcon field="costBasis" />
                </button>
              </th>
              <th className="text-right p-4 text-sm font-medium text-muted-foreground">
                <button 
                  onClick={() => handleSort('unrealizedPnL')}
                  className="flex items-center justify-end space-x-2 hover:text-foreground transition-standard w-full"
                >
                  <span>P&L</span>
                  <SortIcon field="unrealizedPnL" />
                </button>
              </th>
              <th className="text-right p-4 text-sm font-medium text-muted-foreground">
                <button 
                  onClick={() => handleSort('allocation')}
                  className="flex items-center justify-end space-x-2 hover:text-foreground transition-standard w-full"
                >
                  <span>Allocation</span>
                  <SortIcon field="allocation" />
                </button>
              </th>
              <th className="text-center p-4 text-sm font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedHoldings?.map((holding) => (
              <tr key={holding?.id} className="border-b border-border hover:bg-muted/30 transition-standard">
                <td className="p-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                      <span className="text-xs font-bold text-primary">
                        {holding?.symbol?.slice(0, 2)}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-card-foreground">{holding?.symbol}</p>
                      <p className="text-xs text-muted-foreground">{holding?.name}</p>
                    </div>
                  </div>
                </td>
                <td className="p-4 text-right">
                  <p className="font-medium text-card-foreground">
                    {holding?.quantity?.toLocaleString('en-US', { maximumFractionDigits: 8 })}
                  </p>
                </td>
                <td className="p-4 text-right">
                  <p className="font-medium text-card-foreground">
                    {formatCurrency(holding?.currentPrice)}
                  </p>
                  <p className={`text-xs ${getPerformanceColor(holding?.priceChange24h)}`}>
                    {formatPercentage(holding?.priceChange24h)}
                  </p>
                </td>
                <td className="p-4 text-right">
                  <p className="font-medium text-card-foreground">
                    {formatCurrency(holding?.value)}
                  </p>
                </td>
                <td className="p-4 text-right">
                  <p className="font-medium text-card-foreground">
                    {formatCurrency(holding?.costBasis)}
                  </p>
                </td>
                <td className="p-4 text-right">
                  <p className={`font-medium ${getPerformanceColor(holding?.unrealizedPnL)}`}>
                    {formatCurrency(Math.abs(holding?.unrealizedPnL))}
                  </p>
                  <p className={`text-xs ${getPerformanceColor(holding?.unrealizedPnLPercent)}`}>
                    {formatPercentage(holding?.unrealizedPnLPercent)}
                  </p>
                </td>
                <td className="p-4 text-right">
                  <div className="flex items-center justify-end space-x-2">
                    <div className="w-12 bg-muted rounded-full h-2">
                      <div 
                        className="bg-primary h-2 rounded-full"
                        style={{ width: `${Math.min(holding?.allocation, 100)}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium text-card-foreground">
                      {holding?.allocation?.toFixed(1)}%
                    </span>
                  </div>
                </td>
                <td className="p-4">
                  <div className="flex items-center justify-center space-x-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Edit"
                      onClick={() => onEditPosition(holding)}
                      className="h-8 w-8 p-0"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="TrendingUp"
                      onClick={() => window.location.href = '/trading-signals'}
                      className="h-8 w-8 p-0"
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {sortedHoldings?.length === 0 && (
        <div className="p-12 text-center">
          <Icon name="PieChart" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-card-foreground mb-2">No holdings found</h3>
          <p className="text-muted-foreground mb-4">
            {filterType === 'all' ?'Start building your portfolio by adding your first position.'
              : `No ${filterType} found in your current holdings.`
            }
          </p>
          <Button variant="default" iconName="Plus" iconPosition="left" onClick={onAddPosition}>
            Add Your First Position
          </Button>
        </div>
      )}
    </div>
  );
};

export default HoldingsTable;